import React, { useState, useEffect, useRef } from 'react';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import { SearchIcon } from '../../utils/icons';

// Scrollable container with fade gradients
const ScrollableWithFade = ({ children, className = '', style = {} }) => {
  const scrollRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      // Check initial state
      handleScroll();
      scrollElement.addEventListener('scroll', handleScroll);
      // Also check on window resize
      window.addEventListener('resize', handleScroll);
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, []);

  const handleMouseDown = (e) => {
    const ele = scrollRef.current;
    if (!ele) return;

    ele.style.cursor = 'grabbing';
    const startX = e.pageX - ele.offsetLeft;
    const scrollLeft = ele.scrollLeft;

    const handleMouseMove = (e) => {
      const x = e.pageX - ele.offsetLeft;
      const walk = (x - startX) * 2;
      ele.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      ele.style.cursor = 'grab';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div style={{ position: 'relative' }}>
      {showLeftFade && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '80px',
            background: `linear-gradient(to right, ${colors.bg.primary}, transparent)`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}
      {showRightFade && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '80px',
            background: `linear-gradient(to left, ${colors.bg.primary}, transparent)`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}
      <div
        ref={scrollRef}
        className={className}
        style={style}
        onMouseDown={handleMouseDown}
      >
        {children}
      </div>
    </div>
  );
};

// Generate random gradient for avatars
const getRandomGradient = (seed) => {
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)',
    'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
    'linear-gradient(135deg, #f77062 0%, #fe5196 100%)',
  ];

  // Use seed to deterministically select gradient
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return gradients[Math.abs(hash) % gradients.length];
};

// Playlist Card Component
const PlaylistCard = ({ playlist }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: '160px',
        width: '160px',
        cursor: 'pointer',
        gap: 0,
        alignItems: 'center',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Back card layer 1 */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            width: '128px',
            height: '128px',
            borderRadius: '12px',
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${playlist.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(0.7)',
            }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.2)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} />
        </div>

        {/* Middle card layer 2 */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            marginTop: '-120px',
            width: '144px',
            height: '144px',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0px -2px 2px rgba(0,0,0,0.5)',
            transition: 'all 0.3s ease-in-out',
            transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${playlist.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(0.8)',
            }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.2)' }} />
        </div>

        {/* Front card layer 3 */}
        <div
          style={{
            position: 'relative',
            zIndex: 3,
            marginTop: '-130px',
            marginBottom: '16px',
            width: '160px',
            height: '160px',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0px -2px 2px rgba(0,0,0,0.5)',
            transition: 'transform 0.3s ease-in-out',
            transform: isHovered ? 'translateY(-12px)' : 'translateY(0)',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${playlist.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(8px)',
              padding: '4px 8px',
              borderRadius: '6px',
            }}
          >
            <div style={{ color: 'white', width: '12px', height: '12px' }}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
              </svg>
            </div>
            <span style={{ fontSize: '12px', color: 'white', fontWeight: '500' }}>
              {playlist.likes}
            </span>
          </div>
        </div>
      </div>

      {/* Playlist info */}
      <div style={{ gap: '4px', alignItems: 'flex-start', width: '100%', display: 'flex', flexDirection: 'column' }}>
        <p style={{ ...typography.medium.title, color: colors.text.primary, marginBottom: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
          {playlist.name}
        </p>
        <p style={{ ...typography.small.regular, color: colors.text.tertiary, marginBottom: 0 }}>
          {playlist.songs}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div
            style={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: getRandomGradient(playlist.creator),
            }}
          />
          <span style={{ ...typography.small.regular, color: colors.text.tertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {playlist.creator}
          </span>
        </div>
      </div>
    </div>
  );
};

// Mock data
const mockSongs = [
  { id: '1', title: 'Escape', artist: 'doeeyes', genre: 'Alternative, emo, indie rock', plays: '23k', likes: '22', comments: '0', image: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=400&h=400&fit=crop' },
  { id: '2', title: 'Nonsensical Groove', artist: 'analogsphnx', genre: 'Alternative, emo, indie rock', plays: '23k', likes: '22', comments: '0', image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop' },
  { id: '3', title: 'Espana 12', artist: 'ghostplayer', genre: 'Alternative, emo, indie rock', plays: '23k', likes: '22', comments: '0', image: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400&h=400&fit=crop' },
  { id: '4', title: 'Midnight dance', artist: 'doeeyes', genre: 'Alternative, emo, indie rock', plays: '23k', likes: '22', comments: '0', image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=400&h=400&fit=crop' },
  { id: '5', title: 'A long journey', artist: 'analogsphnx', genre: 'Alternative, emo, indie rock', plays: '23k', likes: '22', comments: '0', image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400&h=400&fit=crop' },
  { id: '6', title: 'Summer Nights', artist: 'ghostplayer', genre: 'Electronic, synthwave', plays: '45k', likes: '38', comments: '2', image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=400&h=400&fit=crop' },
  { id: '7', title: 'Urban Echo', artist: 'doeeyes', genre: 'Hip hop, trap', plays: '67k', likes: '52', comments: '5', image: 'https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=400&h=400&fit=crop' },
  { id: '8', title: 'Crystal Sky', artist: 'analogsphnx', genre: 'Ambient, downtempo', plays: '12k', likes: '15', comments: '1', image: 'https://images.unsplash.com/photo-1498038432885-c6f3f1b912ee?w=400&h=400&fit=crop' },
];

const mockArtists = [
  { id: '1', name: 'Marius renaud', handle: '@marius', followers: '5.2k followers', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop' },
  { id: '2', name: 'Danny', handle: '@danrd', followers: '5.2k followers', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop' },
  { id: '3', name: 'Kimber', handle: '@kmbr', followers: '5.2k followers', image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop' },
  { id: '4', name: 'Andrea Carter', handle: '@acarter', followers: '5.2k followers', image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop' },
  { id: '5', name: 'Atr/rW/rks', handle: '@afterworks', followers: '5.2k followers', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop' },
  { id: '6', name: 'aux moi', handle: '@auxmoi', followers: '2k followers', image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop' },
  { id: '7', name: 'Reene', handle: '@reene', followers: '5.2k followers', image: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=200&h=200&fit=crop' },
];

const mockPlaylists = [
  { id: '1', name: 'On the move', songs: '20 songs', creator: 'analogsphnx', likes: 49, image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=400&h=400&fit=crop' },
  { id: '2', name: 'Ballads', songs: '19 songs', creator: 'ghostplayer', likes: 49, image: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400&h=400&fit=crop' },
  { id: '3', name: 'Latenight', songs: '10 songs', creator: 'doeeyes', likes: 49, image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=400&h=400&fit=crop' },
  { id: '4', name: 'Fast and Electric', songs: '17 songs', creator: 'analogsphnx', likes: 49, image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400&h=400&fit=crop' },
  { id: '5', name: 'Moody', songs: '26 songs', creator: 'ghostplayer', likes: 49, image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=400&fit=crop' },
  { id: '6', name: 'Potions', songs: '22 songs', creator: 'doeeyes', likes: 49, image: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=400&h=400&fit=crop' },
  { id: '7', name: 'Sky surfer', songs: '8 songs', creator: 'analogsphnx', likes: 49, image: 'https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=400&h=400&fit=crop' },
];

const mockGenres = [
  { id: '1', name: 'Funk', image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop' },
  { id: '2', name: 'Metal', image: 'https://images.unsplash.com/photo-1498038432885-c6f3f1b912ee?w=400&h=300&fit=crop' },
  { id: '3', name: 'EDM', image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=400&h=300&fit=crop' },
  { id: '4', name: 'Rap', image: 'https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=400&h=300&fit=crop' },
  { id: '5', name: 'Rock', image: 'https://images.unsplash.com/photo-1498038432885-c6f3f1b912ee?w=400&h=300&fit=crop' },
  { id: '6', name: 'Pop', image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400&h=300&fit=crop' },
  { id: '7', name: 'Instrumental', image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=400&h=300&fit=crop' },
  { id: '8', name: 'Country', image: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400&h=300&fit=crop' },
  { id: '9', name: 'Jazz', image: 'https://images.unsplash.com/photo-1415201364774-f6f0bb35f28f?w=400&h=300&fit=crop' },
];

const Search = ({ hasPlaybar = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [displayedQuery, setDisplayedQuery] = useState('');
  const [randomSeed, setRandomSeed] = useState(0);
  const searchInputRef = useRef(null);

  // Shuffle array based on a seed for consistent randomization per search
  const shuffleArray = (array, seed) => {
    const arr = [...array];
    let currentIndex = arr.length;
    let randomIndex;

    // Simple seeded random function
    const seededRandom = (max, s) => {
      const x = Math.sin(s++) * 10000;
      return Math.floor((x - Math.floor(x)) * max);
    };

    while (currentIndex !== 0) {
      randomIndex = seededRandom(currentIndex, seed + currentIndex);
      currentIndex--;
      [arr[currentIndex], arr[randomIndex]] = [arr[randomIndex], arr[currentIndex]];
    }

    return arr;
  };

  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'songs', label: 'Songs' },
    { key: 'artists', label: 'Artists' },
    { key: 'playlists', label: 'Playlists' },
    { key: 'genres', label: 'Genres' },
  ];

  // Auto-focus the search input when component mounts
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Fake loading when search changes
  useEffect(() => {
    if (searchQuery !== displayedQuery) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setDisplayedQuery(searchQuery);
        setRandomSeed(Date.now()); // Generate new random seed for each search
        setIsLoading(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, displayedQuery]);

  // Helper function to intelligently modify names based on search query
  const modifyName = (original, query) => {
    if (!query || query.length < 2) return original;

    const words = query.toLowerCase().split(' ').filter(w => w.length > 0);
    const mainWord = words[0];

    // Capitalize first letter
    const capitalizedQuery = mainWord.charAt(0).toUpperCase() + mainWord.slice(1);

    // Add search term to the beginning or incorporate it
    const variations = [
      `${capitalizedQuery} ${original}`,
      `${original} ${capitalizedQuery}`,
      `${capitalizedQuery}'s ${original}`,
      `${original} (${capitalizedQuery})`,
    ];

    // Use hash of original name to consistently pick same variation
    const hash = original.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return variations[hash % variations.length];
  };

  // Always show all content, but modify names based on search query and randomize order
  const filteredSongs = shuffleArray(
    mockSongs.map(song => ({
      ...song,
      title: displayedQuery ? modifyName(song.title, displayedQuery) : song.title,
      artist: displayedQuery ? modifyName(song.artist, displayedQuery) : song.artist,
    })),
    randomSeed
  );

  const filteredArtists = shuffleArray(
    mockArtists.map(artist => ({
      ...artist,
      name: displayedQuery ? modifyName(artist.name, displayedQuery) : artist.name,
    })),
    randomSeed + 1
  );

  const filteredPlaylists = shuffleArray(
    mockPlaylists.map(playlist => ({
      ...playlist,
      name: displayedQuery ? modifyName(playlist.name, displayedQuery) : playlist.name,
    })),
    randomSeed + 2
  );

  const filteredGenres = shuffleArray(
    mockGenres.map(genre => ({
      ...genre,
      name: displayedQuery && displayedQuery.length > 2 ? modifyName(genre.name, displayedQuery) : genre.name,
    })),
    randomSeed + 3
  );

  // Common genre keywords to detect genre-related searches (50 genres)
  const genreKeywords = [
    'rock', 'pop', 'jazz', 'blues', 'country', 'folk', 'metal', 'punk', 'indie', 'alternative',
    'electronic', 'edm', 'house', 'techno', 'trance', 'dubstep', 'drum and bass', 'ambient', 'downtempo', 'chillout',
    'hip hop', 'rap', 'trap', 'r&b', 'soul', 'funk', 'disco', 'reggae', 'ska', 'dancehall',
    'classical', 'opera', 'symphony', 'instrumental', 'acoustic', 'lofi', 'synthwave', 'vaporwave', 'shoegaze', 'grunge',
    'emo', 'hardcore', 'screamo', 'post-rock', 'prog rock', 'psychedelic', 'garage', 'britpop', 'k-pop', 'j-pop'
  ];

  // Common first names to detect artist/people searches (100 names)
  const nameKeywords = [
    'james', 'john', 'robert', 'michael', 'william', 'david', 'richard', 'joseph', 'thomas', 'charles',
    'daniel', 'matthew', 'anthony', 'mark', 'paul', 'steven', 'andrew', 'kenneth', 'joshua', 'kevin',
    'mary', 'patricia', 'jennifer', 'linda', 'barbara', 'elizabeth', 'susan', 'jessica', 'sarah', 'karen',
    'nancy', 'lisa', 'betty', 'margaret', 'sandra', 'ashley', 'kimberly', 'emily', 'donna', 'michelle',
    'emma', 'olivia', 'sophia', 'isabella', 'mia', 'charlotte', 'amelia', 'harper', 'evelyn', 'abigail',
    'christopher', 'ryan', 'ethan', 'jacob', 'nicholas', 'tyler', 'brandon', 'alexander', 'jonathan', 'justin',
    'benjamin', 'samuel', 'noah', 'logan', 'nathan', 'caleb', 'christian', 'austin', 'elijah', 'lucas',
    'grace', 'victoria', 'natalie', 'hannah', 'chloe', 'madison', 'alexis', 'samantha', 'taylor', 'rachel',
    'lauren', 'anna', 'brianna', 'kayla', 'julia', 'alyssa', 'megan', 'lily', 'ella', 'zoe',
    'logan', 'jackson', 'mason', 'liam', 'carter', 'wyatt', 'jayden', 'dylan', 'luke', 'henry'
  ];

  // Check if the search query matches any genre or genre keyword
  const isGenreSearch = displayedQuery && displayedQuery.trim().length > 0 && (
    mockGenres.some(genre => genre.name.toLowerCase().includes(displayedQuery.toLowerCase().trim())) ||
    genreKeywords.some(keyword => displayedQuery.toLowerCase().trim().includes(keyword) || keyword.includes(displayedQuery.toLowerCase().trim()))
  );

  // Check if the search query matches any artist name or name keyword
  const isArtistSearch = !isGenreSearch && displayedQuery && displayedQuery.trim().length > 0 && (
    mockArtists.some(artist => artist.name.toLowerCase().includes(displayedQuery.toLowerCase().trim())) ||
    nameKeywords.some(name => displayedQuery.toLowerCase().trim().includes(name) || name.includes(displayedQuery.toLowerCase().trim()))
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: colors.bg.primary,
        color: colors.text.primary,
        width: '100%',
        overflowY: 'auto',
        paddingBottom: hasPlaybar ? '80px' : '0',
      }}
    >
      <style>
        {`
          .search-container {
            max-width: min(1200px, calc(100vw - 221px));
            margin: 0 auto;
            padding: 16px;
            width: 100%;
          }
          @media (max-width: 768px) {
            .search-container {
              max-width: 100vw;
              padding: 16px;
            }
          }
        `}
      </style>
      <div className="search-container">
        {/* Search Input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '0 12px',
            borderRadius: '100px',
            border: `1px solid ${colors.bg.secondary}`,
            backgroundColor: colors.bg.fog.thin,
            height: '40px',
            marginBottom: '32px',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: '16px',
              height: '16px',
              color: colors.text.inactive,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.94453 11.8891C5.5626 11.8891 4.39303 11.4105 3.43582 10.4532C2.47861 9.49604 2 8.32647 2 6.94453C2 5.5626 2.47861 4.39303 3.43582 3.43582C4.39303 2.47861 5.5626 2 6.94453 2C8.32647 2 9.49604 2.47861 10.4532 3.43582C11.4105 4.39303 11.8891 5.5626 11.8891 6.94453C11.8891 7.50238 11.8003 8.02853 11.6228 8.52298C11.4453 9.01743 11.2044 9.45483 10.9002 9.83518L13.7908 12.7258C13.9303 12.8653 14 13.0428 14 13.2583C14 13.4739 13.9303 13.6513 13.7908 13.7908C13.6513 13.9303 13.4739 14 13.2583 14C13.0428 14 12.8653 13.9303 12.7258 13.7908L9.83518 10.9002C9.45483 11.2044 9.01743 11.4453 8.52298 11.6228C8.02853 11.8003 7.50238 11.8891 6.94453 11.8891ZM6.94453 10.3677C7.8954 10.3677 8.70365 10.0349 9.36926 9.36926C10.0349 8.70365 10.3677 7.8954 10.3677 6.94453C10.3677 5.99366 10.0349 5.18542 9.36926 4.51981C8.70365 3.8542 7.8954 3.52139 6.94453 3.52139C5.99366 3.52139 5.18542 3.8542 4.51981 4.51981C3.8542 5.18542 3.52139 5.99366 3.52139 6.94453C3.52139 7.8954 3.8542 8.70365 4.51981 9.36926C5.18542 10.0349 5.99366 10.3677 6.94453 10.3677Z" fill="currentColor"/>
            </svg>
          </div>
          <input
            ref={searchInputRef}
            placeholder="Search for songs, playlists, users, or genres"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: colors.text.primary,
              ...typography.small.title,
              caretColor: colors.accent,
            }}
          />
          <style>
            {`
              .search-input::placeholder {
                color: ${colors.bg.fog.dense} !important;
              }
            `}
          </style>
        </div>

        {/* Tabs */}
        <div
          style={{
            width: '100%',
            overflowX: 'auto',
            borderBottom: `1px solid ${colors.bg.secondary}`,
          }}
        >
          <div style={{ display: 'flex', minWidth: 'auto' }}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              const count =
                tab.key === 'songs' ? filteredSongs.length :
                tab.key === 'artists' ? filteredArtists.length :
                tab.key === 'playlists' ? filteredPlaylists.length :
                tab.key === 'genres' ? filteredGenres.length :
                0;

              return (
                <div
                  key={tab.key}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '40px',
                    padding: '0 16px',
                    cursor: 'pointer',
                    position: 'relative',
                    color: isActive ? colors.text.primary : colors.text.tertiary,
                    transition: 'color 0.2s',
                  }}
                  onClick={() => setActiveTab(tab.key)}
                  onMouseEnter={(e) => !isActive && (e.currentTarget.style.color = colors.text.primary)}
                  onMouseLeave={(e) => !isActive && (e.currentTarget.style.color = colors.text.tertiary)}
                >
                  <p style={{ ...typography.small.regular, fontWeight: 500 }}>
                    {tab.label}
                  </p>
                  {isActive && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        backgroundColor: 'white',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Results */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
              <div className="spinner" style={{
                width: '40px',
                height: '40px',
                border: '4px solid rgba(255,255,255,0.1)',
                borderTop: '4px solid ' + colors.text.primary,
                borderRadius: '50%',
                animation: 'spin 0.65s linear infinite'
              }} />
              <p style={{ ...typography.medium.regular, color: colors.text.tertiary }}>
                Searching...
              </p>
            </div>
          </div>
        ) : (
          <div style={{ padding: '24px 0', width: '100%', overflow: 'hidden' }}>
            {/* All Tab */}
            {activeTab === 'all' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                {/* Genres Section - Show first if genre search */}
                {isGenreSearch && filteredGenres.length > 0 && (
                  <div>
                    <h2 className="text-xl font-medium text-white mb-3">
                      Genres
                    </h2>
                    <ScrollableWithFade
                      className="scrollbar-hide"
                      style={{
                        display: 'flex',
                        gap: '16px',
                        overflowX: 'auto',
                        cursor: 'grab',
                        paddingTop: '4px',
                        paddingBottom: '4px',
                        marginTop: '-4px',
                        marginBottom: '-4px',
                      }}
                    >
                      {filteredGenres.slice(0, 9).map((genre) => (
                        <div
                          key={genre.id}
                          style={{
                            minWidth: '200px',
                            height: '120px',
                            position: 'relative',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              backgroundImage: `url(${genre.image})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)',
                            }}
                          />
                          <div
                            style={{
                              position: 'relative',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'flex-end',
                              padding: '12px',
                            }}
                          >
                            <span style={{ ...typography.medium.title, color: 'white' }}>
                              {genre.name}
                            </span>
                          </div>
                        </div>
                      ))}
                    </ScrollableWithFade>
                  </div>
                )}

                {/* Artists Section - Show first if artist search */}
                {isArtistSearch && filteredArtists.length > 0 && (
                  <div>
                    <h2 className="text-xl font-medium text-white mb-3">
                      Artists
                    </h2>
                    <ScrollableWithFade
                      className="scrollbar-hide"
                      style={{
                        display: 'flex',
                        gap: '16px',
                        overflowX: 'auto',
                        cursor: 'grab',
                      }}
                    >
                      {filteredArtists.slice(0, 7).map((artist) => (
                        <div
                          key={artist.id}
                          style={{
                            minWidth: '164px',
                            width: '164px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0,
                            alignItems: 'flex-start',
                            padding: '12px',
                            borderRadius: '8px',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = colors.bg.glass.thin}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <div
                            style={{
                              width: '140px',
                              height: '140px',
                              borderRadius: '50%',
                              overflow: 'hidden',
                              backgroundColor: colors.bg.tertiary,
                              backgroundImage: `url(${artist.image})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start', width: '100%' }}>
                            <p style={{ ...typography.medium.title, color: colors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                              {artist.name}
                            </p>
                            <p style={{ ...typography.small.regular, color: colors.text.tertiary }}>
                              {artist.handle}
                            </p>
                            <p style={{ ...typography.small.regular, color: colors.text.tertiary }}>
                              {artist.followers}
                            </p>
                          </div>
                        </div>
                      ))}
                    </ScrollableWithFade>
                  </div>
                )}

                {/* Songs Section */}
                {filteredSongs.length > 0 && (
                  <div>
                    <h2 className="text-xl font-medium text-white mb-3">
                      Songs
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {filteredSongs.slice(0, 5).map((song) => (
                        <div
                          key={song.id}
                          style={{
                            padding: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            borderRadius: '8px',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = colors.bg.glass.thin}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <div
                            style={{
                              width: '56px',
                              height: '56px',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              flexShrink: 0,
                              backgroundImage: `url(${song.image})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ ...typography.medium.title, color: colors.text.primary, marginBottom: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {song.title}
                            </p>
                            <p style={{ ...typography.small.regular, color: colors.text.tertiary, marginBottom: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {song.genre}
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <div
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  background: getRandomGradient(song.artist),
                                }}
                              />
                              <span style={{ ...typography.small.regular, color: colors.text.tertiary }}>
                                {song.artist}
                              </span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '16px', color: colors.text.tertiary }}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <div style={{ width: '14px', height: '14px' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </div>
                              <span style={{ ...typography.small.regular }}>{song.plays}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <div style={{ width: '14px', height: '14px' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
                                </svg>
                              </div>
                              <span style={{ ...typography.small.regular }}>{song.likes}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <div style={{ width: '14px', height: '14px' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                                </svg>
                              </div>
                              <span style={{ ...typography.small.regular }}>{song.comments}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Artists Section */}
                {!isArtistSearch && filteredArtists.length > 0 && (
                  <div>
                    <h2 className="text-xl font-medium text-white mb-3">
                      Artists
                    </h2>
                    <ScrollableWithFade
                      className="scrollbar-hide"
                      style={{
                        display: 'flex',
                        gap: '16px',
                        overflowX: 'auto',
                        cursor: 'grab',
                      }}
                    >
                      {filteredArtists.slice(0, 7).map((artist) => (
                        <div
                          key={artist.id}
                          style={{
                            minWidth: '164px',
                            width: '164px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0,
                            alignItems: 'flex-start',
                            padding: '12px',
                            borderRadius: '8px',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = colors.bg.glass.thin}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <div
                            style={{
                              width: '140px',
                              height: '140px',
                              borderRadius: '50%',
                              overflow: 'hidden',
                              backgroundColor: colors.bg.tertiary,
                              backgroundImage: `url(${artist.image})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start', width: '100%' }}>
                            <p style={{ ...typography.medium.title, color: colors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                              {artist.name}
                            </p>
                            <p style={{ ...typography.small.regular, color: colors.text.tertiary }}>
                              {artist.handle}
                            </p>
                            <p style={{ ...typography.small.regular, color: colors.text.tertiary }}>
                              {artist.followers}
                            </p>
                          </div>
                        </div>
                      ))}
                    </ScrollableWithFade>
                  </div>
                )}

                {/* Playlists Section */}
                {filteredPlaylists.length > 0 && (
                  <div>
                    <h2 className="text-xl font-medium text-white mb-3">
                      Playlists
                    </h2>
                    <ScrollableWithFade
                      className="scrollbar-hide"
                      style={{
                        display: 'flex',
                        gap: '16px',
                        overflowX: 'auto',
                        cursor: 'grab',
                      }}
                    >
                      {filteredPlaylists.slice(0, 7).map((playlist) => (
                        <PlaylistCard key={playlist.id} playlist={playlist} />
                      ))}
                    </ScrollableWithFade>
                  </div>
                )}

                {/* Genres Section */}
                {!isGenreSearch && filteredGenres.length > 0 && (
                  <div>
                    <h2 className="text-xl font-medium text-white mb-3">
                      Genres
                    </h2>
                    <ScrollableWithFade
                      className="scrollbar-hide"
                      style={{
                        display: 'flex',
                        gap: '16px',
                        overflowX: 'auto',
                        cursor: 'grab',
                        paddingTop: '4px',
                        paddingBottom: '4px',
                        marginTop: '-4px',
                        marginBottom: '-4px',
                      }}
                    >
                      {filteredGenres.slice(0, 9).map((genre) => (
                        <div
                          key={genre.id}
                          style={{
                            minWidth: '200px',
                            height: '120px',
                            position: 'relative',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              backgroundImage: `url(${genre.image})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)',
                            }}
                          />
                          <div
                            style={{
                              position: 'relative',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'flex-end',
                              padding: '12px',
                            }}
                          >
                            <span style={{ ...typography.medium.title, color: 'white' }}>
                              {genre.name}
                            </span>
                          </div>
                        </div>
                      ))}
                    </ScrollableWithFade>
                  </div>
                )}
              </div>
            )}

            {/* Songs Tab */}
            {activeTab === 'songs' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {filteredSongs.map((song) => (
                  <div
                    key={song.id}
                    style={{
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      borderRadius: '8px',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = colors.bg.glass.thin}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        flexShrink: 0,
                        backgroundImage: `url(${song.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ ...typography.medium.title, color: colors.text.primary, marginBottom: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {song.title}
                      </p>
                      <p style={{ ...typography.small.regular, color: colors.text.tertiary, marginBottom: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {song.genre}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: getRandomGradient(song.artist),
                          }}
                        />
                        <span style={{ ...typography.small.regular, color: colors.text.tertiary }}>
                          {song.artist}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', color: colors.text.tertiary }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <div style={{ width: '14px', height: '14px' }}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                        <span style={{ ...typography.small.regular }}>{song.plays}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <div style={{ width: '14px', height: '14px' }}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
                          </svg>
                        </div>
                        <span style={{ ...typography.small.regular }}>{song.likes}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <div style={{ width: '14px', height: '14px' }}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                          </svg>
                        </div>
                        <span style={{ ...typography.small.regular }}>{song.comments}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Artists Tab */}
            {activeTab === 'artists' && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(164px, 1fr))',
                  gap: '24px',
                }}
              >
                {filteredArtists.map((artist) => (
                  <div
                    key={artist.id}
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0,
                      alignItems: 'flex-start',
                      padding: '12px',
                      borderRadius: '8px',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = colors.bg.glass.thin}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div
                      style={{
                        width: '140px',
                        height: '140px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        backgroundColor: colors.bg.tertiary,
                        backgroundImage: `url(${artist.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start', width: '100%' }}>
                      <p style={{ ...typography.medium.title, color: colors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {artist.name}
                      </p>
                      <p style={{ ...typography.small.regular, color: colors.text.tertiary }}>
                        {artist.handle}
                      </p>
                      <p style={{ ...typography.small.regular, color: colors.text.tertiary }}>
                        {artist.followers}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Playlists Tab */}
            {activeTab === 'playlists' && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: '24px',
                }}
              >
                {filteredPlaylists.map((playlist) => (
                  <PlaylistCard key={playlist.id} playlist={playlist} />
                ))}
              </div>
            )}

            {/* Genres Tab */}
            {activeTab === 'genres' && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '16px',
                }}
              >
                {filteredGenres.map((genre) => (
                  <div
                    key={genre.id}
                    style={{
                      height: '120px',
                      position: 'relative',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${genre.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)',
                      }}
                    />
                    <div
                      style={{
                        position: 'relative',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'flex-end',
                        padding: '12px',
                      }}
                    >
                      <span style={{ ...typography.medium.title, color: 'white' }}>
                        {genre.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add keyframes for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Search;
